import { z } from "zod";

// ─── Step 1: Legal Representative ─────────────────────────────────────────────

export const step1Schema = z.object({
  legal_name_chinese: z.string().min(1, "common.required"),
  legal_name_english: z.string().min(1, "common.required"),
  nationality: z.string().min(1, "common.required"),
  role_in_company: z.string().min(1, "common.required"),
  department: z.string().min(1, "common.required"),
  contact_phone: z.string().min(1, "common.required"),
  contact_email: z
    .union([z.string().email("preVetting.step1.error.invalidEmail"), z.literal("")])
    .optional(),
  legal_representative_id_type: z.enum(["passport", "national_id", "other"]),
  legal_representative_id_document: z
    .string()
    .nullable()
    .refine((v) => v !== null && v.length > 0, {
      message: "preVetting.step1.error.idDocumentRequired",
    }),
  wechat_qr_string: z
    .string()
    .nullable()
    .refine((v) => v !== null, {
      message: "preVetting.step1.error.wechatQrNull",
    }),
});

export type Step1Values = z.infer<typeof step1Schema>;

// ─── Step 2: Business Information ─────────────────────────────────────────────

export const step2Schema = z.object({
  company_name_chinese: z.string().min(1, "common.required"),
  company_name_english: z.string().min(1, "common.required"),
  company_name_arabic: z.string().optional(),
  hq_address: z.string().min(1, "common.required"),
  hq_city: z.string().min(1, "common.required"),
  hq_postal_code: z.string().min(1, "common.required"),
  unified_social_credit_code: z
    .string()
    .length(18, "preVetting.step2.error.unifiedCodeLength")
    .regex(/^[0-9A-Z]+$/, "preVetting.step2.error.unifiedCodeFormat"),
  business_license_document: z
    .string()
    .nullable()
    .refine((v) => v !== null && v.length > 0, {
      message: "preVetting.step2.error.businessLicenseRequired",
    }),
});

export type Step2Values = z.infer<typeof step2Schema>;

// ─── Step 3: Product Categories ────────────────────────────────────────────────

export const step3Schema = z.object({
  industry: z.array(z.string()).min(1, "preVetting.step3.error.industryRequired"),
  product_subcategories: z.array(z.string()).min(1, "preVetting.step3.error.subcategoryRequired"),
  iso_certification_document: z.string().nullable().optional(),
  ce_marking_document: z.string().nullable().optional(),
  quality_test_report_document: z.string().nullable().optional(),
  eco_certification_document: z.string().nullable().optional(),
});

export type Step3Values = z.infer<typeof step3Schema>;

// ─── Step 4: Payment Channels ──────────────────────────────────────────────────

const bankDetailsSchema = z.object({
  bank_account_number: z.string().min(1, "common.required"),
  bank_account_holder_name: z.string().min(1, "common.required"),
  bank_iban_code: z.string().optional(),
  bank_swift_code: z.string().optional(),
  bank_address: z.string().min(1, "common.required"),
  bank_settlement_currency: z.string().min(1, "common.required"),
  is_bank_account_under_legal_entity_name: z.boolean(),
});

const alipayDetailsSchema = z.object({
  alipay_merchant_id: z.string().min(1, "common.required"),
  alipay_account_holder_name: z.string().min(1, "common.required"),
  alipay_account_email: z.string().email("preVetting.step1.error.invalidEmail"),
  alipay_settlement_currency: z.string().min(1, "common.required"),
  is_alipay_account_under_legal_entity_name: z.boolean(),
});

const wechatPayDetailsSchema = z.object({
  wechatpay_merchant_id: z.string().min(1, "common.required"),
  wechatpay_account_holder_name: z.string().min(1, "common.required"),
  wechatpay_account_email: z.string().email("preVetting.step1.error.invalidEmail"),
  wechatpay_settlement_currency: z.string().min(1, "common.required"),
  wechatpay_account_type: z.string().min(1, "common.required"),
  wechatpay_qr_string: z
    .string()
    .nullable()
    .refine((v) => v !== null, {
      message: "preVetting.step4.wechatPay.error.qrNull",
    }),
});

export const step4Schema = z.preprocess(
  (raw: unknown) => {
    const data = raw as Record<string, unknown>;
    if (!data || !Array.isArray(data.payment_channels)) return raw;
    // Strip sub-objects for deselected channels before field-level validation
    // so that a residual partial default value doesn't trigger required-field errors.
    return {
      ...data,
      bank: data.payment_channels.includes("bank") ? data.bank : undefined,
      alipay: data.payment_channels.includes("alipay") ? data.alipay : undefined,
      wechat_pay: data.payment_channels.includes("wechat_pay") ? data.wechat_pay : undefined,
    };
  },
  z
    .object({
      payment_channels: z
        .array(z.enum(["bank", "alipay", "wechat_pay", "union_pay"]))
        .min(1, "preVetting.step4.error.channelRequired"),
      bank: bankDetailsSchema.optional(),
      alipay: alipayDetailsSchema.optional(),
      wechat_pay: wechatPayDetailsSchema.optional(),
    })
    .superRefine((data, ctx) => {
      if (data.payment_channels.includes("bank") && !data.bank) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "preVetting.step4.error.bankRequired",
          path: ["bank"],
        });
      }
      if (data.payment_channels.includes("alipay") && !data.alipay) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "preVetting.step4.error.alipayRequired",
          path: ["alipay"],
        });
      }
      if (data.payment_channels.includes("wechat_pay") && !data.wechat_pay) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "preVetting.step4.error.wechatPayRequired",
          path: ["wechat_pay"],
        });
      }
    })
);

export type Step4Values = z.infer<typeof step4Schema>;

// ─── Step 5: Branding ─────────────────────────────────────────────────────────

export const step5Schema = z.object({
  business_short_name_english: z.string().min(1, "common.required"),
  business_short_name_arabic: z.string().optional(),
  business_description_english: z.string().min(10, "preVetting.step5.error.descriptionTooShort"),
  business_description_arabic: z.string().optional(),
  primary_industry: z.string().min(1, "common.required"),
  business_logo: z
    .string()
    .nullable()
    .refine((v) => v !== null && v.length > 0, {
      message: "preVetting.step5.error.logoRequired",
    }),
});

export type Step5Values = z.infer<typeof step5Schema>;

// ─── Combined ──────────────────────────────────────────────────────────────────

export const preVettingSchema = z.object({
  step1: step1Schema,
  step2: step2Schema,
  step3: step3Schema,
  step4: step4Schema,
  step5: step5Schema,
});

export type PreVettingValues = z.infer<typeof preVettingSchema>;
